import React, { Component } from 'react'
import PropTypes from 'prop-types'
import AmCharts from '@amcharts/amcharts3-react'
import AmChartsRadar from 'amcharts3/amcharts/radar' //eslint-disable-line
import OutsideClickHandler from 'react-outside-click-handler'
import classnames from 'classnames'
import _ from 'lodash'

import { reduceData } from './helpers'
import { getGraphConfig } from './graphConfig'

import ToggleButton from '../../../ToggleButton'
import DataTable from './components/DataTable'

import './style.scss'

export default class RadarGraph extends Component {
    static propTypes = {
        data: PropTypes.object,
        handleChangeSelectedYear: PropTypes.func.isRequired,
        handleToggleDataTable: PropTypes.func.isRequired,
        id: PropTypes.string.isRequired,
        selectedYear: PropTypes.number,
        showDataTable: PropTypes.bool,
        translates: PropTypes.shape({
            dataTable: PropTypes.oneOfType([PropTypes.string, PropTypes.element]).isRequired,
            firm: PropTypes.oneOfType([PropTypes.string, PropTypes.element]).isRequired,
            sector: PropTypes.oneOfType([PropTypes.string, PropTypes.element]).isRequired,
            firmEvolution: PropTypes.oneOfType([PropTypes.string, PropTypes.element]).isRequired,
            sectorEvolution: PropTypes.oneOfType([PropTypes.string, PropTypes.element]).isRequired,
            sectorAverage: PropTypes.oneOfType([PropTypes.string, PropTypes.element]).isRequired,
            firmVersusSector: PropTypes.oneOfType([PropTypes.string, PropTypes.element]).isRequired,
            pointer: PropTypes.oneOfType([PropTypes.string, PropTypes.element]).isRequired,
            yes: PropTypes.oneOfType([PropTypes.string, PropTypes.element]).isRequired,
            no: PropTypes.oneOfType([PropTypes.string, PropTypes.element]).isRequired,
            noComparisonGroup: PropTypes.oneOfType([PropTypes.string, PropTypes.element]).isRequired,
            numberOfSubjects: PropTypes.oneOfType([PropTypes.string, PropTypes.element]).isRequired,
            sectorNace: PropTypes.oneOfType([PropTypes.string, PropTypes.element]).isRequired,
            noGraphData: PropTypes.oneOfType([PropTypes.string, PropTypes.element]).isRequired,
        }).isRequired,
    }

    constructor(props) {
        super(props)

        this.state = {
            dropdownActive: false,
        }
    }

    onDropdownRowClick = (year) => {
        this.props.handleChangeSelectedYear(year)

        this.handleCloseDropdown()
    }

    handleToggleDropdown = () => {
        const { dropdownActive } = this.state

        this.setState({ dropdownActive: !dropdownActive })
    }

    handleCloseDropdown = () => {
        this.setState({ dropdownActive: false })
    }

    isDataEmpty = (data) => {
        if (_.isEmpty(data)) return true

        return _.every(data, (x) => _.isNil(x))
    }

    renderGraph = (data) => {
        const { selectedYear, translates } = this.props

        const firmData = _.find(_.get(data, 'analysisData', []), { year: selectedYear })
        const sectorData = _.find(_.get(data, 'sector', []), { year: selectedYear })
        const sectionsDefinition = _.get(data, 'legend.sections', [])
        const dataProvider = reduceData(firmData, sectorData, sectionsDefinition)
        const minValue = Math.min(..._.map(dataProvider, (x) => _.get(x, 'firmVersusSector', null)))

        return (
            <AmCharts.React
                style={{
                    height: '550px',
                    width: '582px',
                    padding: '16px 8px 8px 8px',
                }}
                className={'radar-graph'}
                options={getGraphConfig(dataProvider, minValue, translates)}
            />
        )
    }

    render() {
        const { id, selectedYear, showDataTable, data, translates, handleToggleDataTable } = this.props
        const { dropdownActive } = this.state

        const firmData = _.find(_.get(data, 'analysisData', []), { year: selectedYear })
        const sectorData = _.find(_.get(data, 'sector', []), { year: selectedYear })
        const sectionsDefinition = _.get(data, 'legend.sections', [])
        const dataTableData = reduceData(firmData, sectorData, sectionsDefinition)

        const naceCode = _.get(sectorData, 'mainNace.code', '')
        const naceText = _.get(sectorData, 'mainNace.text', '')
        const naceCategory = _.get(sectorData, 'category', null)
        const naceSubjects = `${translates.numberOfSubjects} ${_.get(sectorData, 'subjects', 0)}`
        const naceString = !this.isDataEmpty(_.get(sectorData, 'sectorAverage'))
            ? [`${naceCode} ${naceText}`, naceCategory, naceSubjects].join('; ')
            : `${naceCode} ${naceText}; ${translates.noComparisonGroup}`

        return (
            <div className={'finance-analysis-radar-graph'}>
                <div className={'graph-header-row'}>
                    <span className={'header'}>{translates.dataTable}</span>
                    <ToggleButton
                        active={showDataTable || false}
                        textOff={translates.no}
                        textOn={translates.yes}
                        onClickHandler={handleToggleDataTable}
                        inactiveGreyBackground
                        inactiveGreyBorder
                    />
                </div>
                <div className={'nace-container'}>
                    {translates.sectorNace}
                    <span>{naceString}</span>
                </div>
                <div className={'graph-wrapper'}>
                    <div className={'year-selector-wrapper'}>
                        <span className={'value'}>{selectedYear || ''}</span>
                        <OutsideClickHandler
                            disabled={!dropdownActive}
                            onOutsideClick={() => this.handleCloseDropdown()}
                        >
                            <button className={'dropdown-btn'} onClick={() => this.handleToggleDropdown()} />
                            <div className={classnames('dropdown-menu', dropdownActive && 'active')}>
                                {_.map(_.get(data, 'analysisData', []), (row, index) => (
                                    <div
                                        key={`fanal-radar-graph-dropdown-${index}-row`}
                                        className={'dropdown-row'}
                                        onClick={() => this.onDropdownRowClick(_.get(row, 'year', null))}
                                    >
                                        <span className={'dropdown-text'}>{_.get(row, 'year', null)}</span>
                                    </div>
                                ))}
                            </div>
                        </OutsideClickHandler>
                    </div>
                    {!this.isDataEmpty(_.get(sectorData, 'sectorAverage')) &&
                    !this.isDataEmpty(_.get(firmData, 'values')) ? (
                        this.renderGraph(data)
                    ) : (
                        <div className={'empty-radar-graph'}>
                            <span className={'no-graph-data-msg'}>{translates.noGraphData}</span>
                        </div>
                    )}
                </div>
                {showDataTable &&
                    (!this.isDataEmpty(_.get(sectorData, 'sectorAverage')) ||
                        !this.isDataEmpty(_.get(firmData, 'values'))) && (
                        <div className={'data-table-container'}>
                            <div className={'table-title-wrapper'}>
                                <span className={'table-title'}>{translates.dataTable}</span>
                                <button className={'remove-data-table'} onClick={() => handleToggleDataTable(false)} />
                            </div>
                            <DataTable id={id} data={dataTableData} translates={translates} />
                        </div>
                    )}
            </div>
        )
    }
}
